import { ResponseError } from "@lucaswickstrom/fetch";
import { queryClient, setMessage, useMutationWithError, useQueryWithError, } from "@ssf/components";
import { addPlayer, createClub, createContender, createMember, createMembership, createTeam, getFederation, getMember, getSchool as getMemdbSchool, getPairingsByTournamentsAndContenders, getParticipantsFromTournament, getParticipantsFromTournamentByMember, getSchoolDetails, getTeam, getTournament, getTournamentGroup, getTournamentsByYearAndType, getTournamentsForListing, MemdbBoolean, OrgType, RoleType, removeContender, removePlayer, replaceRole, setMemberPassword, TournamentType, updateClub, updateMember, updateSchoolBreak, } from "@ssf/memdb-api";
import { queryOptions } from "@tanstack/react-query";
import _ from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
export const useUpdateMember = (options) => useMutationWithError(["useUpdateMember"], updateMember, options);
const getMemberContactWithSchema = (memberId) => getMember(memberId, { firstname: 1, email: 1, phone: 1 });
export const useMemberContact = (memberId, options) => useQueryWithError(["member", memberId], () => getMemberContactWithSchema(memberId), options);
const getMemberAddressWithSchema = (memberId) => getMember(memberId, { firstname: 1, street: 1, zipcode: 1, city: 1 });
export const useMemberAddress = (memberId, options) => useQueryWithError(["member", memberId], () => getMemberAddressWithSchema(memberId), options);
function doCheckEmailAndPhone({ orgrole, end, }) {
    return ((orgrole === RoleType.ContactPerson || orgrole === RoleType.TeamLeader1) &&
        end === "1970-01-01");
}
function doCheckAddress({ orgtype, end }) {
    return end === "1970-01-01" && orgtype === OrgType.Club;
}
export const useReplaceRoles = (options) => {
    return useMutationWithError(["useReplaceRoles"], async ([role, ...roles]) => {
        // try the first role, can fail due to missing contact information
        const response = await replaceRole(role, doCheckEmailAndPhone(role), doCheckAddress(role));
        await Promise.all(roles.map((role) => replaceRole(role, doCheckEmailAndPhone(role), doCheckAddress(role))));
        return response;
    }, options, (err) => {
        // Suppress all ResponseErrors - handled by RoleDialog
        if (err instanceof ResponseError) {
            return true;
        }
        return false;
    });
};
export const tournamentsByDateQuery = (type) => queryOptions({
    queryKey: ["tournamentsByDateQuery", type],
    queryFn: () => getTournamentsForListing(type),
});
const getParticipantsFromTournamentsByMemberWithSchema = (tournamentIds, memberId) => Promise.all(tournamentIds.map(async (tournamentId) => await getParticipantsFromTournamentByMember({
    tournamentId,
    memberId,
    forceTeamType: true,
    schema: [
        {
            issues: [1],
            team: {
                id: 1,
                name: 1,
                club: {
                    id: 1,
                    name: 1,
                    active: 1,
                    noeconomy: 1,
                    schoolclub: 1,
                    ...registrationClubTaskSchema,
                    specialType: 1,
                },
            },
            group: {
                id: 1,
                name: 1,
                class: { tournament: { name: 1, type: 1, start: 1 } },
            },
        },
    ],
}).then((contenders) => contenders.map((contender) => ({ ...contender, tournamentId })))));
export const useParticipantsFromTournamentsByMember = (tournamentIds, memberId, options) => {
    const query = useQueryWithError(["useParticipantsFromTournamentsByMember", tournamentIds, memberId], () => getParticipantsFromTournamentsByMemberWithSchema(tournamentIds, memberId), options);
    const contenders = useMemo(() => Object.values(_.groupBy(query.data?.flat().filter(({ team }) => Boolean(team)), "team.id")).map((contenders) => ({
        ...contenders[0],
        groups: contenders.map(({ group }) => group),
    })), [query.data]);
    return { ...query, contenders };
};
const registrationClubTaskSchema = {
    startdate: 1,
    roles: [{ orgrole: 1, filter: "(end=1970-01-01)" }],
    approvalStatus: [
        { referenceid: 1, approved: 1, signed: 1, documentid: 1, updated: 1 },
    ],
    registrations: [
        {
            memberid: 1,
            documentid: 1,
            date: 1,
            filter: `(date>=${new Date().getFullYear()}-01-01)`,
        },
    ],
};
const getMemberClubs = (memberId) => getMember(memberId, {
    name: 1,
    clubs: [
        {
            id: 1,
            name: 1,
            className: 1,
            schoolid: 1,
            active: 1,
            noeconomy: 1,
            schoolclub: 1,
            ...registrationClubTaskSchema,
            specialType: 1,
        },
    ],
    roles: [
        {
            orgid: 1,
            orgtype: 1,
            orgrole: 1,
            end: 1,
            filter: "(&(end=1970-01-01)(orgrole=150)(orgtype=3))",
        },
    ],
});
export const useMemberClubs = (memberId, options) => useQueryWithError(["useMemberClubs", memberId], () => getMemberClubs(memberId), options);
const getParticipantsFromTournamentsWithSchema = (tournamentIds) => Promise.all(tournamentIds.map(async (tournamentId) => await getParticipantsFromTournament({
    tournamentId,
    schema: [
        {
            issues: [1],
            team: {
                id: 1,
                name: 1,
                clubid: 1,
            },
        },
    ],
}).then((contenders) => contenders
    .map((contender) => ({ ...contender, tournamentId }))
    .filter(({ team }) => team?.clubid > 0))));
export const participantsFromTournamentsQuery = (tournamentIds) => queryOptions({
    queryKey: ["participantsFromTournamentsQuery", tournamentIds],
    queryFn: () => getParticipantsFromTournamentsWithSchema(tournamentIds),
});
const getSchoolsWithSchema = (schoolIds, onError) => Promise.all(schoolIds.map((schoolId) => getMemdbSchool(schoolId, {
    name: 1,
    clubs: [
        {
            id: 1,
            name: 1,
            className: 1,
            schoolid: 1,
            active: 1,
            noeconomy: 1,
            schoolclub: 1,
            ...registrationClubTaskSchema,
            specialType: 1,
        },
    ],
}).catch((error) => {
    if (onError?.(error, schoolId))
        return null;
    throw error;
}))).then((schools) => schools.filter((school) => school !== null));
export const useSchools = (schoolIds, options, onError) => useQueryWithError(["useSchools", schoolIds], () => getSchoolsWithSchema(schoolIds, onError), options);
const getSchoolName = (schoolId) => getMemdbSchool(schoolId, { name: 1 });
export const useSchoolName = (schoolId, options) => useQueryWithError(["useSchoolName", schoolId], () => getSchoolName(schoolId), options);
export const useCreateContender = (options) => useMutationWithError(["useCreateContender"], async ({ groupId, updateClubReason, leaderMemberId, ...data }) => {
    const response = await createTeam({
        ...data,
        // Assign the selected leader instead of making the acting admin captain.
        captain: leaderMemberId === undefined ? data.captain : false,
    });
    if (leaderMemberId !== undefined) {
        await replaceRole({
            memberid: leaderMemberId,
            orgid: response.result,
            orgtype: OrgType.Team,
            orgrole: RoleType.TeamLeader1,
            seclevel: data.tournamentid ?? -1,
            start: new Date().toLocaleDateString("sv-SE"),
            end: "1970-01-01",
        });
    }
    await createContender({
        groupid: groupId,
        partid: response.result,
        teamnumber: 1,
        type: OrgType.Team,
        regdate: new Date().toLocaleDateString("sv-SE"),
    });
    if (updateClubReason) {
        await updateClub(updateClubReason);
    }
    return response;
}, options);
const getTeamWithSchema = (teamId, tournamentId, regEndDate) => getTeam({
    teamId,
    params: {
        schema: {
            id: 1,
            name: 1,
            classsize: 1,
            school: {
                id: 1,
                name: 1,
                breakyear: 1,
                springbreak: 1,
                easterbreak: 1,
            },
            club: {
                id: 1,
                name: 1,
                active: 1,
                street: 1,
                zipcode: 1,
                city: 1,
                specialType: 1,
                startdate: 1,
                clubStatus: [{ status: 1, year: 1, orderedGift: 1 }],
                approvalStatus: [
                    {
                        referenceid: 1,
                        approved: 1,
                        signed: 1,
                        documentid: 1,
                        updated: 1,
                    },
                ],
                roles: [
                    {
                        memberid: 1,
                        end: 1,
                        orgrole: 1,
                        filter: `(&(end=1970-01-01)(orgrole=${RoleType.Admin}))`,
                    },
                ],
                registrations: [
                    {
                        member: { id: 1, name: 1, birthday: 1 },
                        membershiptype: 1,
                        documentid: 1,
                        regend: 1,
                        filter: `(regend>=${regEndDate ?? new Date().toLocaleDateString("sv-SE")})`,
                        date: 1,
                    },
                ],
                district: { id: 1, active: 1 },
            },
            participations: [
                {
                    partid: 1,
                    teamnumber: 1,
                    groupid: 1,
                    issues: [1],
                    atvenue: 1,
                    group: {
                        id: 1,
                        name: 1,
                        status: 1,
                        schoolclubtournament: 1,
                        classListRequired: 1,
                        boards: 1,
                        start: 1,
                        class: {
                            tournament: {
                                id: 1,
                                name: 1,
                                type: 1,
                                start: 1,
                                end: 1,
                                online: 1,
                                isadmin: 1,
                            },
                        },
                    },
                },
            ],
            roles: [
                {
                    orgid: 1,
                    orgrole: 1,
                    orgtype: 1,
                    start: 1,
                    end: 1,
                    seclevel: 1,
                    member: { id: 1, name: 1 },
                    email: 1,
                    phone: 1,
                    filter: `(&(orgrole=201)(end=1970-01-01)(|(seclevel=${tournamentId})(seclevel=-1)))`,
                },
            ],
            players: [
                {
                    member: { id: 1, name: 1, sex: 1 },
                    tournamentid: 1,
                    filter: `(|(tournamentid=${tournamentId})(seclevel=-1))`,
                },
            ],
        },
    },
});
export const useTeam = (teamId, tournamentId, options, regEndDate) => useQueryWithError(["useTeam", teamId, tournamentId, regEndDate], () => getTeamWithSchema(teamId, tournamentId, regEndDate), options);
export const useRemoveContender = (options) => useMutationWithError(["useRemoveContender"], removeContender, options);
const setMembersPassword = ({ members, password, }) => Promise.all(members.map((id) => setMemberPassword({ id, password })));
export const useSetMembersPassword = (options) => useMutationWithError(["useSetMembersPassword"], setMembersPassword, options);
const addPlayerWithParticipationCheck = async (reg, alreadyExistsMessage) => {
    const contenders = await getParticipantsFromTournamentByMember({
        memberId: reg.memberid,
        tournamentId: reg.tournamentid,
        forceTeamType: true,
        schema: [{ partid: 1, name: 1 }],
    });
    if (contenders.length) {
        throw new Error(alreadyExistsMessage(contenders.map(({ name }) => name.trim()).join(", ")));
    }
    return addPlayer(reg);
};
export const useAddPlayer = (options) => {
    const { t } = useTranslation("useAddPlayer");
    return useMutationWithError(["useAddPlayer"], (reg) => addPlayerWithParticipationCheck(reg, (teamName) => t("alreadyExists", { teamName })), options);
};
export const useUpdateTeamPlayers = (teamId, tournamentId) => {
    const { t } = useTranslation("useAddPlayer");
    return useMutationWithError(["useUpdateTeamPlayers", teamId, tournamentId], async ({ addIds, removeIds, }) => {
        const addedIds = [];
        const removedIds = [];
        const failures = [];
        const date = new Date().toLocaleDateString("sv-SE");
        for (const memberid of new Set([...addIds, ...removeIds])) {
            try {
                if (addIds.includes(memberid)) {
                    await addPlayerWithParticipationCheck({ memberid, orgid: teamId, tournamentid: tournamentId, date }, (teamName) => t("alreadyExists", { teamName }));
                    addedIds.push(memberid);
                }
                else {
                    await removePlayer({ teamId, memberId: memberid });
                    removedIds.push(memberid);
                }
            }
            catch (error) {
                failures.push({
                    memberId: memberid,
                    message: error instanceof Error
                        ? (await setMessage(error).catch(() => error)).message
                        : t("failed"),
                });
            }
        }
        return { addedIds, removedIds, failures };
    }, {
        onSettled: () => queryClient.invalidateQueries({ queryKey: ["useTeam", teamId] }),
    });
};
export const useRemovePlayer = (options) => useMutationWithError(["useRemovePlayer"], removePlayer, options);
export const useUpdateSchoolBreak = (options) => useMutationWithError(["useUpdateSchoolBreak"], updateSchoolBreak, options);
const getTournamentGroupWithSchema = (groupId) => getTournamentGroup({
    groupId,
    params: {
        schema: {
            id: 1,
            name: 1,
            pairingsystem: 1,
            currentround: 1,
            topdelimiter: 1,
            contenders: [
                {
                    partid: 1,
                    team: {
                        name: 1,
                        school: { name: 1, districtid: 1 },
                        club: {
                            id: 1,
                            name: 1,
                            active: 1,
                            schoolclub: 1,
                            noeconomy: 1,
                            ...registrationClubTaskSchema,
                            specialType: 1,
                            clubStatus: [{ status: 1, year: 1, orderedGift: 1 }],
                        },
                    },
                    result: {
                        place: 1,
                        wins: 1,
                        draws: 1,
                        losses: 1,
                        secpoints: 1,
                        points: 1,
                    },
                    issues: [1],
                    withdrawn: 1,
                    atvenue: 1,
                    regdate: 1,
                    sort: [["result.place", "asc"]],
                    filter: "(result=*)",
                },
            ],
            pairings: [
                {
                    id: 1,
                    finalized: 1,
                    round: 1,
                    awayid: 1,
                    homeid: 1,
                    publishednote: 1,
                    date: 1,
                    awayresult: 1,
                    homeresult: 1,
                },
            ],
            class: { tournament: { type: 1 } },
        },
    },
});
export const useTournamentGroup = (groupId, options) => useQueryWithError(["useTournamentGroup", groupId], () => getTournamentGroupWithSchema(groupId), options);
const getDistrictsWithSchema = () => getFederation({
    districts: [{ id: 1, name: 1 }],
}).then((data) => data.districts);
export const useDistricts = (options) => useQueryWithError(["useDistricts"], () => getDistrictsWithSchema(), options);
const createClubWithRoles = async ({ memberId, ...club }) => {
    const { result } = await createClub(club);
    const contactRole = {
        memberid: memberId,
        orgid: result,
        orgtype: OrgType.Club,
        orgrole: RoleType.ContactPerson,
        seclevel: -1,
        start: new Date().toLocaleDateString("sv-SE"),
        end: "1970-01-01",
    };
    await Promise.all([
        replaceRole(contactRole),
        replaceRole({
            ...contactRole,
            orgrole: RoleType.President,
        }),
    ]);
    return { result };
};
export const useCreateClub = (options) => useMutationWithError(["useCreateClub"], createClubWithRoles, options);
export const useSchool = (schoolCode) => useQueryWithError(["schoolDetails", schoolCode], () => getSchoolDetails(Number(schoolCode)), { enabled: !!schoolCode });
export const useCreateMembership = (options) => useMutationWithError(["useCreateMembership"], createMembership, options);
export const useCreateMember = (options) => useMutationWithError(["useCreateMember"], createMember, options);
const usePairingsByTournamentsAndContendersWithSchema = (params) => getPairingsByTournamentsAndContenders({
    ...params,
    schema: [
        {
            id: 1,
            home: {
                partid: 1,
                withdrawn: 1,
                team: { name: 1, school: { name: 1 } },
            },
            homeresult: 1,
            away: {
                partid: 1,
                withdrawn: 1,
                team: { name: 1, school: { name: 1 } },
            },
            awayresult: 1,
            date: 1,
            finalized: 1,
            publishednote: 1,
            group: { class: { tournament: { id: 1 } } },
        },
    ],
});
export const usePairingsByTournamentsAndContenders = (params, options) => useQueryWithError(["usePairingsByTournamentsAndContenders", params], () => usePairingsByTournamentsAndContendersWithSchema(params), options);
const getTournamentWithSchema = (tournamentId) => getTournament({
    tournamentId,
    params: {
        schema: {
            id: 1,
            name: 1,
            type: 1,
            start: 1,
            invitationurl: 1,
            message: 1,
            online: 1,
            classes: [
                {
                    id: 1,
                    name: 1,
                    order: 1,
                    groups: [
                        {
                            id: 1,
                            name: 1,
                            pairingsystem: 1,
                            currentround: 1,
                            order: 1,
                            status: 1,
                            classListRequired: 1,
                            sort: [["order"], ["id"]],
                        },
                    ],
                    sort: [
                        ["order", "desc"],
                        ["id", "desc"],
                    ],
                },
            ],
        },
    },
});
export const useTournament = (tournamentId, options) => useQueryWithError(["useTournament", tournamentId], () => getTournamentWithSchema(tournamentId), options);
const getY2cTournamentsWithSchema = () => getTournamentsByYearAndType({
    year: new Date().getFullYear(),
    type: TournamentType.Yes2Chess,
    schema: [
        {
            id: 1,
            name: 1,
            type: 1,
            start: 1,
            classes: [
                {
                    id: 1,
                    groups: [
                        {
                            id: 1,
                            name: 1,
                            status: 1,
                        },
                    ],
                },
            ],
        },
    ],
});
export const useY2cStatus = (options) => {
    const query = useQueryWithError(["Y2CTournaments"], getY2cTournamentsWithSchema, options);
    const y2cIsOpen = useMemo(() => {
        if (!query.data)
            return true;
        return query.data.some((tournament) => {
            return tournament.classes.some((cls) => cls.groups.some((group) => group.status === MemdbBoolean.True));
        });
    }, [query.data]);
    return {
        ...query,
        y2cIsOpen,
    };
};
//# sourceMappingURL=api.js.map